import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IAIInvoicesService } from "services/AIInvoicesService";
import { ProcessedInvoiceModel, NotificationStyles, NotificationTypes } from "services/api/cplapi/CPLAPI";
import { IAuthTestService } from "services/AuthTestService";
import { ITestService } from "services/TestService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

type ComponentsTestStateType = {
  ProcessingInvoice: boolean;
  InvoiceResult: ProcessedInvoiceModel | undefined;
};

const initialState: ComponentsTestStateType = {
  ProcessingInvoice: false,
  InvoiceResult: undefined,
};

export const componentsTestPageSlice = createSlice({
  name: "componentsTest",
  initialState,
  reducers: {
    resetInvoiceResult: (state) => {
      state.InvoiceResult = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(processInvoice.pending, (state) => {
        state.ProcessingInvoice = true;
      })
      .addCase(processInvoice.rejected, (state) => {
        state.ProcessingInvoice = false;
      })
      .addCase(processInvoice.fulfilled, (state, action) => {
        state.ProcessingInvoice = false;
        state.InvoiceResult = action.payload;
      });
  },
});

export const testCPLSignalRNotification = createAsyncThunk<
  void,
  { notificationType: NotificationTypes; notificationStyle: NotificationStyles },
  { state: RootState }
>("componentsTest/testCPLSignalRNotification", async ({ notificationType, notificationStyle }, thunkAPI) => {
  const testService = container.get<ITestService>(SERVICE_TYPES.TestService);
  const state = thunkAPI.getState();
  return await testService.testSignalRNotification(state.auth.user?.userID ?? "", notificationType, notificationStyle);
});

export const testAuthSignalRNotification = createAsyncThunk<
  void,
  { notificationType: NotificationTypes; notificationStyle: NotificationStyles },
  { state: RootState }
>("componentsTest/testAuthSignalRNotification", async ({ notificationType, notificationStyle }, thunkAPI) => {
  const testService = container.get<IAuthTestService>(SERVICE_TYPES.AuthTestService);
  const state = thunkAPI.getState();
  return await testService.testSignalRNotification(state.auth.user?.userID ?? "", notificationType, notificationStyle);
});

export const createCompletedBackgroundProcess = createAsyncThunk(
  "componentsTest/createCompletedBackgroundProcess",
  async () => {
    const testService = container.get<ITestService>(SERVICE_TYPES.TestService);
    return await testService.createCompletedBackgroundProcess();
  }
);

export const createFailedBackgroundProcess = createAsyncThunk(
  "componentsTest/createFailedBackgroundProcess",
  async () => {
    const testService = container.get<ITestService>(SERVICE_TYPES.TestService);
    return await testService.createFailedBackgroundProcess();
  }
);

export const sendDocumentToPrint = createAsyncThunk("componentsTest/sendDocumentToPrint", async () => {
  const testService = container.get<ITestService>(SERVICE_TYPES.TestService);
  return await testService.sendDocumentToPrint();
});

export const generateServiceChargeInvoice = createAsyncThunk(
  "componentsTest/generateServiceChargeInvoice",
  async () => {
    const testService = container.get<ITestService>(SERVICE_TYPES.TestService);
    return await testService.generateServiceChargeInvoice();
  }
);
export const generateAnticipatedExpenditureReport = createAsyncThunk(
  "componentsTest/generateAnticipatedExpenditureReport",
  async () => {
    const testService = container.get<ITestService>(SERVICE_TYPES.TestService);
    return await testService.generateAnticipatedExpenditureReport();
  }
);

export const processInvoice = createAsyncThunk("componentsTest/processInvoice", async (file: File) => {
  const aiInvoicesService = container.get<IAIInvoicesService>(SERVICE_TYPES.AIInvoicesService);
  return await aiInvoicesService.processInvoice(file);
});

export const selectProcessingInvoice = (state: RootState) => {
  return state.componentsTest.ProcessingInvoice;
};

export const selectInvoiceResult = (state: RootState) => {
  return state.componentsTest.InvoiceResult;
};

export const { resetInvoiceResult } = componentsTestPageSlice.actions;

export default componentsTestPageSlice.reducer;
